/**
 * PerLetterAttack — each letter tile fires individually to the enemy.
 * Letters fire from last to first with staggered timing.
 * Each hit deals its letter's Scrabble value; attack bonus goes on the final hit.
 */

import { AttackType, AttackContext, HitCallback } from "./AttackType";

export class PerLetterAttack implements AttackType {
  readonly id = "per-letter";
  readonly name = "Letter Barrage";

  private staggerDelay = 80;
  private flightDuration = 250;

  execute(
    ctx: AttackContext,
    onHit: HitCallback,
    onComplete: () => void,
  ): void {
    const { scene, tiles, target, totalDamage } = ctx;

    // Fire order: last tile first (unravels from end)
    const fireOrder = [...tiles].reverse();
    const lastIndex = fireOrder.length - 1;
    let completedCount = 0;

    // Distribute totalDamage proportionally by letter value
    const rawSum = fireOrder.reduce((s, t) => s + t.value, 0) || 1;
    let assigned = 0;
    const tileDamages = fireOrder.map((tile, i) => {
      if (i === lastIndex) return totalDamage - assigned; // remainder on last tile
      const d = Math.floor(totalDamage * tile.value / rawSum);
      assigned += d;
      return d;
    });

    fireOrder.forEach((tile, i) => {
      const tileDamage = tileDamages[i];

      // Random scatter around the enemy so damage numbers don't stack
      const offsetX = (Math.random() - 0.5) * 40;
      const offsetY = (Math.random() - 0.5) * 30;
      const hitX = target.x + offsetX;
      const hitY = target.y + offsetY;

      scene.time.delayedCall(i * this.staggerDelay, () => {
        // Animate each object in this tile
        for (const obj of tile.objects) {
          scene.tweens.add({
            targets: obj,
            x: hitX,
            y: hitY,
            scaleX: 0.6,
            scaleY: 0.6,
            alpha: 0,
            duration: this.flightDuration,
            ease: "Power2",
          });
        }

        // On impact (after flight completes)
        scene.time.delayedCall(this.flightDuration, () => {
          // Clean up tile objects
          for (const obj of tile.objects) {
            obj.destroy();
          }

          onHit(tileDamage, hitX, hitY - 30);
          completedCount++;

          if (completedCount === fireOrder.length) {
            onComplete();
          }
        });
      });
    });
  }
}
